import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Send, Sparkles, RotateCcw, MessageSquareText, ShieldCheck, X, Inbox, BookOpen } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useIsMounted } from '@/hooks/useIsMounted';
import AdminSupportInbox from '@/components/support/AdminSupportInbox';
import FlightHandoffCard from '@/components/support/FlightHandoffCard';
import ChatFlightOptions from '@/components/support/ChatFlightOptions';
import { getSpecialist, CONCIERGE_SPECIALISTS } from '@/constants/conciergeSpecialists';
import {
  TEAM_AUTHOR_DEFAULT,
  TEAM_AVATARS,
  TEAM_POLL_MS,
  appendTeamBubbles,
  bubbleFromServer,
  bubbleTime,
  historyForDesk,
  mergeConversation,
  oldestSeq,
  prependEarlier,
} from '@/lib/supportConversation';
import { listenForSignals } from '@/lib/realtimeSignals';
import {
  buildSmsUrl,
  SUPPORT_SMS_DISPLAY,
  TOMMY_ASK_PARAM,
} from '@/constants/support';
import { MESSAGE_MAX_CHARS, MESSAGE_TOO_LONG } from '@/constants/messageLimits';
import { chatDraftFromSearch } from '@/utils/chatTopic';

// Storage keys for the on-device transcript + threadId. We NEVER use
// these prefixes verbatim — the actual key is suffixed with the
// signed-in user's Supabase auth.uid (see `storageKeysFor` below).
// That partitioning is the privacy boundary on shared browsers: when
// User A signs out and User B signs in, we read/write from a totally
// different localStorage namespace and User A's transcript can't be
// rendered into User B's session.
const STORAGE_KEY_PREFIX = 'defera.tommy.chat.v1';
const THREAD_ID_KEY_PREFIX = 'defera.tommy.threadId.v1';
// Old global keys (pre user-scoping). We scrub these on first mount
// of every authenticated session so they can't continue to leak past
// data into other users' tabs.
const LEGACY_STORAGE_KEYS = ['defera.tommy.chat.v1', 'defera.tommy.threadId.v1'];

function storageKeysFor(userId) {
  if (!userId) return null;
  return {
    transcript: `${STORAGE_KEY_PREFIX}::${userId}`,
    threadId: `${THREAD_ID_KEY_PREFIX}::${userId}`,
  };
}
const MAX_HISTORY = 50;

/**
 * Mint a thread id the first time the user actually says something. We
 * use crypto.randomUUID when available (modern browsers) and fall back
 * to a Math.random / Date-based UUID-shaped string in the rare case the
 * Crypto API is unavailable. The server validates the v4 hex shape and
 * silently skips persistence on mismatch, so an old/bad value here just
 * means the thread doesn't show up in the admin inbox.
 */
function newThreadId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to manual UUID below
  }
  const hex = (n) => Math.floor(Math.random() * 16 ** n).toString(16).padStart(n, '0');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${hex(3)}-${hex(12)}`;
}

/** The signed-in customer's bearer header, or null. */
async function currentAuthHeader() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token ? `Bearer ${data.session.access_token}` : null;
  } catch {
    return null;
  }
}

/** A server message as a bubble, with the specialist who spoke it. */
const toBubble = (m) => bubbleFromServer(m, { specialistFor: getSpecialist });

/**
 * How the "Text a human" pill becomes visible.
 *
 * **The server decides. Not this file.**
 *
 * This used to be a pile of regexes — one set matching customers asking
 * for a person ("can I talk to a human?"), another matching Tommy's own
 * canonical handoff phrases lifted out of his system prompt ("Text a
 * human", "loop our concierge in", the support number itself). It worked,
 * but it wired a client-side regex directly to prompt copy living in
 * server/tommy-concierge.js: reword the prompt and the button silently
 * stopped appearing, with nothing failing loudly enough to notice.
 *
 * It's gone for a second, larger reason. Tommy no longer decides who
 * reaches a human — his manager Stephanie does, via the consult triage
 * (server/tommy-consult.js). Most things she answers outright; things
 * needing a person but not *now* she takes into the team's 24h queue; only
 * genuinely time-sensitive situations get the live concierge. Inferring
 * the pill from chat text would have let it appear on situations she'd
 * already decided to queue — handing the customer a live human she
 * deliberately didn't spend, which is the whole cost this is meant to cut.
 *
 * So: the SSE `done` event carries `handoffUnlocked`. Once unlocked it stays
 * unlocked for the rest of the thread (re-litigating on every turn would be
 * maddening for someone already in a bad moment), and it resets on **New
 * chat** with the rest of the state. The server also re-asserts prior
 * consult unlocks on every later `done`, so a page reload (which resets
 * this component's sticky state) gets the pill back on the next reply.
 *
 * The server sets it on any of three conditions:
 *   1. Stephanie's consult returned `urgent` — the original, primary route.
 *   2. The consult `failed`/`inconclusive` — fails open, because nothing was
 *      filed and the customer shouldn't pay for our outage.
 *   3. The customer has **insisted** on a person (`insistsOnLiveAgent` in
 *      server/tommy-concierge.js) — asked in two separate turns, or once with
 *      an insistence marker ("I already asked for a live agent"). Since
 *      2026-08-04 the second ask doesn't have to restate the first: once one
 *      explicit ask exists, follow-ups like "text me please", "how do I talk
 *      to them", or a bare "human" count too, and a customer typing the
 *      button's own label ("text a human") unlocks on the spot — thread
 *      60ec049d asked four times in four shapes and only the first counted.
 *
 * (3) was added 2026-07-25 and reverses what used to be a deliberate
 * consequence: a single "I want a human" still does NOT summon the button —
 * that turn gets an honest answer about there being no live agent on chat,
 * plus the tracked-request offer — but insisting now unlocks it regardless of
 * how Stephanie triaged the case. She could previously queue someone who had
 * plainly asked for a person, leaving them no route to a human at all (thread
 * d9f6d409). Her verdict is still the primary path; it is no longer the only
 * one. Detection is deterministic and server-side, never a client regex, for
 * the reasons in the paragraphs above.
 *
 * The words agree with the button by construction: every turn's system
 * prompt states outright whether the pill is on the customer's screen
 * (locked / visible-from-earlier / insisted — see the liveAgentBlock in
 * server/tommy-concierge.js), so the model can neither point at a control
 * that isn't there (the 60ec049d failure) nor deny one that is.
 */

/**
 * Resolve whether a Supabase user is an admin. Mirrors the server's
 * `isUserAdmin` check — admin status comes ONLY from the secure
 * `app_metadata.roles` array (never from `user_metadata`, which is
 * user-editable). Returns false for null / signed-out callers.
 *
 * This is the UI-routing source of truth on the Support page. The
 * `defera.user` localStorage cache that Layout maintains can lag in
 * the same tab when a user signs out and a different user signs in
 * (storage events only fire in OTHER tabs), so we derive admin
 * status from the live Supabase session here instead. The server
 * re-verifies admin on every API call regardless.
 */
function isSessionAdmin(user) {
  if (!user) return false;
  const roles = user?.app_metadata?.roles;
  if (Array.isArray(roles) && roles.includes('admin')) return true;
  return false;
}

// buildSmsUrl / the concierge number now live in src/constants/support.js
// — this page is the ONLY legitimate caller of buildSmsUrl, and only after
// Stephanie has triaged a thread as urgent. See that module's doc.

/**
 * Wrap the AI-generated context into a friendly SMS so the human concierge
 * can immediately see (a) that the message originated from a Tommy hand-off
 * and (b) the customer's actual situation.
 */
function composeHandoffSms({ summary, userFirstName, specialistName = 'Tommy' }) {
  const greeting = userFirstName
    ? `Hi, this is ${userFirstName}. I was just chatting with ${specialistName} on Defera and need a hand.`
    : `Hi, I was just chatting with ${specialistName} on Defera and need a hand.`;
  if (!summary) {
    return `${greeting}`;
  }
  return `${greeting}\n\nContext: ${summary}`;
}

const QUICK_PROMPTS = [
  { icon: Sparkles, label: 'What is Defera Zero?', prompt: 'What is Defera Zero and which airlines support it?' },
  { icon: Sparkles, label: 'How do installments work?', prompt: 'How do the installment payment plans work? Is there a credit check?' },
  { icon: Sparkles, label: 'Where is my confirmation?', prompt: "I booked a trip but I haven't received my airline confirmation number yet. Why?" },
  { icon: Sparkles, label: 'Cancel or change a booking', prompt: 'How do I cancel or change a booking I made through Defera?' },
];

// The "Pull up my trip" picker dialog lived here (along with the departure-date
// formatter that only its cards used). Both of its triggers
// (the header chip and the welcome card) are gone now that the concierge
// preloads every trip on the account before its first reply, so the dialog
// was unreachable. The deep link from a trip page — /support?bookingId=…
// — still focuses a single trip; it calls /api/support-chat/load-my-booking
// directly and never used this component.

/**
 * Tommy's "thinking" indicator — three pulsing dots, the chat-industry standard.
 */
function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-1 py-2" aria-label="Your concierge is typing">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-white/55"
          animate={{ opacity: [0.25, 1, 0.25], y: [0, -2, 0] }}
          transition={{ duration: 1.05, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

/**
 * Lightweight Markdown renderer — supports the small subset Tommy emits
 * (paragraphs, **bold**, *italic*, bullet lists with `-`, and inline `code`).
 * We intentionally avoid a full Markdown library to keep the bundle tiny
 * and the rendering perfectly controlled for the chat aesthetic.
 */
function MarkdownChat({ text }) {
  const blocks = useMemo(() => {
    const lines = text.replace(/\r\n/g, '\n').split('\n');
    const out = [];
    let para = [];
    let list = [];

    const flushPara = () => {
      if (para.length) {
        out.push({ type: 'p', content: para.join(' ') });
        para = [];
      }
    };
    const flushList = () => {
      if (list.length) {
        out.push({ type: 'ul', items: list });
        list = [];
      }
    };

    for (const raw of lines) {
      const line = raw.trimEnd();
      if (/^\s*[-•]\s+/.test(line)) {
        flushPara();
        list.push(line.replace(/^\s*[-•]\s+/, ''));
      } else if (line.trim() === '') {
        flushPara();
        flushList();
      } else {
        flushList();
        para.push(line);
      }
    }
    flushPara();
    flushList();
    return out;
  }, [text]);

  const renderInline = (str) => {
    const parts = [];
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|https?:\/\/[^\s)]+)/g;
    let last = 0;
    let m;
    let key = 0;
    while ((m = regex.exec(str)) !== null) {
      if (m.index > last) parts.push(<span key={key++}>{str.slice(last, m.index)}</span>);
      const t = m[0];
      if (t.startsWith('**')) {
        parts.push(<strong key={key++} className="font-semibold text-white">{t.slice(2, -2)}</strong>);
      } else if (t.startsWith('*')) {
        parts.push(<em key={key++} className="italic">{t.slice(1, -1)}</em>);
      } else if (t.startsWith('`')) {
        parts.push(
          <code key={key++} className="px-1.5 py-0.5 rounded bg-white/[0.08] text-white/95 text-[0.92em] font-mono">
            {t.slice(1, -1)}
          </code>
        );
      } else {
        parts.push(
          <a key={key++} href={t} target="_blank" rel="noreferrer" className="underline decoration-white/30 hover:decoration-white/80">
            {t}
          </a>
        );
      }
      last = m.index + t.length;
    }
    if (last < str.length) parts.push(<span key={key++}>{str.slice(last)}</span>);
    return parts;
  };

  return (
    <div className="space-y-2.5">
      {blocks.map((b, i) =>
        b.type === 'p' ? (
          <p key={i} className="leading-relaxed">{renderInline(b.content)}</p>
        ) : (
          <ul key={i} className="space-y-1.5 pl-0">
            {b.items.map((item, j) => (
              <li key={j} className="flex items-start gap-2.5">
                <span className="mt-2 w-1 h-1 rounded-full bg-white/45 flex-shrink-0" />
                <span className="leading-relaxed flex-1">{renderInline(item)}</span>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}

/**
 * User-side chat row — right-aligned, no avatar, soft royal-blue bubble.
 * Industry-standard asymmetric corner radii so the bubble "tails" toward
 * the speaker.
 *
 * Wrapped in `React.memo` below so typing in the composer (which
 * changes `input` state on every keystroke and re-renders Support)
 * doesn't force every prior bubble — including its Framer-Motion
 * animation reconciliation — to re-run. On mobile with a long
 * thread this was the cause of laggy typing.
 */
function UserBubbleInner({ message }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
      className="flex gap-2.5 justify-end"
    >
      <div className="flex flex-col max-w-[82%] sm:max-w-[75%] items-end">
        <div
          className="px-4 py-2.5 text-[15px] text-white rounded-[22px] rounded-br-[6px] whitespace-pre-wrap break-words"
          style={{
            background:
              'linear-gradient(135deg, hsl(235, 80%, 30%) 0%, hsl(233, 75%, 23%) 100%)',
            boxShadow:
              '0 1px 0 rgba(255,255,255,0.10) inset, 0 6px 18px -8px rgba(15, 40, 138, 0.5)',
          }}
        >
          {message.content}
        </div>
        {message.timestamp && (
          <span className="text-[11px] text-white/30 mt-1 pr-1.5">
            {bubbleTime(message.timestamp)}
          </span>
        )}
      </div>
    </motion.div>
  );
}
const UserBubble = React.memo(UserBubbleInner);

/**
 * Tommy's profile photo, shared between the message bubble, the chat
 * header, and the welcome card. Lives in `/public/tommy.jpg` so Vite
 * serves it directly at `/tommy.jpg` in dev and production.
 */
function TommyAvatar({
  specialist = CONCIERGE_SPECIALISTS[0],
  sizeClass = 'w-8 h-8',
  ringClass = 'ring-1 ring-white/15',
  shadowClass = 'shadow-md',
  ariaHidden = false,
}) {
  return (
    <img
      src={specialist.avatar}
      alt={ariaHidden ? '' : specialist.name}
      aria-hidden={ariaHidden || undefined}
      draggable={false}
      style={{ objectPosition: specialist.avatarPosition || 'center' }}
      className={`${sizeClass} ${ringClass} ${shadowClass} rounded-full object-cover select-none`}
    />
  );
}

/**
 * Tommy bubble that renders rich Markdown content — used for the assistant
 * side. We keep the user bubble plain (single line of text) since users
 * never paste Markdown into chat.
 *
 * Wrapped in `React.memo` below. Without it, every keystroke in the
 * composer would force every prior Tommy bubble to re-render its
 * Markdown + Framer-Motion animation state — the single biggest
 * source of typing lag on mobile threads with several replies.
 * Default referential equality on `message` is enough because we
 * only mutate the streaming bubble's reference (see the setMessages
 * pattern in sendMessage) and `isStreaming` is only true for the
 * actively-streaming row.
 */
function TommyBubbleInner({ message, isStreaming, specialist, onPickFlightOption, flightPickDisabled }) {
  // Each message keeps the face and name of the specialist who wrote it. The
  // header shows who is at the desk now, which can differ once the desk has
  // rotated on a returning customer (founder, 2026-10-30).
  const speaker = message?.specialist || specialist;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
      className="flex gap-2.5 justify-start"
    >
      <div className="flex-shrink-0 self-end mb-0.5">
        <TommyAvatar specialist={speaker} sizeClass="w-8 h-8" />
      </div>
      <div className="flex flex-col max-w-[82%] sm:max-w-[75%] items-start">
        <div className="px-4 py-3 text-[15px] text-white/95 rounded-[22px] rounded-bl-[6px] bg-white/[0.06] backdrop-blur-sm border border-white/[0.06] shadow-sm">
          {message.content ? (
            <MarkdownChat text={message.content} />
          ) : (
            <TypingDots />
          )}
          {isStreaming && message.content && (
            <span
              className="inline-block w-[7px] h-[15px] align-middle ml-0.5 bg-white/65 rounded-[1px]"
              style={{ animation: 'tommy-blink 1.05s steps(2, start) infinite' }}
            />
          )}
        </div>
        {message.flightOptions && (
          <ChatFlightOptions
            options={message.flightOptions}
            onPick={onPickFlightOption}
            disabled={flightPickDisabled}
          />
        )}
        {message.handoff && <FlightHandoffCard handoff={message.handoff} />}
        <span className="text-[11px] text-white/30 mt-1 pl-1.5">
          {speaker.name}{message.timestamp ? ` · ${bubbleTime(message.timestamp)}` : ''}
        </span>
      </div>
    </motion.div>
  );
}
const TommyBubble = React.memo(TommyBubbleInner);

/**
 * A message from the Defera team: a person, or Stephanie when she wrote it
 * (server/support-conversation.js). The team posts here when it answers a
 * request or writes to the customer, and emails a link back to this chat.
 * Left side like the concierge, but signed above the bubble and tinted in
 * brand blue, so the team's word never reads as the AI's.
 */
function TeamBubbleInner({ message }) {
  const label = message.authorLabel || TEAM_AUTHOR_DEFAULT;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
      className="flex gap-2.5 justify-start"
    >
      <div className="flex-shrink-0 self-end mb-0.5">
        <img
          src={TEAM_AVATARS[label] || TEAM_AVATARS[TEAM_AUTHOR_DEFAULT]}
          alt=""
          aria-hidden
          draggable={false}
          className="w-8 h-8 rounded-full object-cover ring-1 ring-white/15 shadow-md select-none"
        />
      </div>
      <div className="flex flex-col max-w-[82%] sm:max-w-[75%] items-start">
        <span className="text-[12px] font-medium text-white/75 mb-1 pl-1.5">{label}</span>
        <div className="px-4 py-3 text-[15px] text-white/95 rounded-[22px] rounded-bl-[6px] bg-brand-600/[0.16] border border-brand-600/40 shadow-sm">
          <MarkdownChat text={message.content} />
        </div>
        {message.timestamp && (
          <span className="text-[11px] text-white/30 mt-1 pl-1.5">
            {bubbleTime(message.timestamp)}
          </span>
        )}
      </div>
    </motion.div>
  );
}
TeamBubbleInner.propTypes = {
  message: PropTypes.shape({
    authorLabel: PropTypes.string,
    content: PropTypes.string,
    timestamp: PropTypes.number,
  }).isRequired,
};
const TeamBubble = React.memo(TeamBubbleInner);

/**
 * ChatComposer — owns the input field state in isolation from the rest
 * of the Support tree.
 *
 * Why this exists: typing in the composer needs to be perceptibly
 * instant on mobile, even after a long conversation. If the input
 * value lived on Support, every keystroke would re-render the whole
 * Support tree — including the (memoized but non-trivial) message
 * list, the welcome card, the Helmet, etc.
 * Even with React.memo on the bubbles, walking and skipping that
 * tree on every keystroke costs real ms on mid-range phones.
 *
 * With the composer extracted, typing only re-renders this small
 * subtree. The parent Support component sees no change at all.
 *
 * Props are intentionally minimal and referentially stable:
 *   - `disabled` flips only when streaming starts/stops.
 *   - `isPreparingHandoff` flips only around the handoff fetch.
 *   - `onSend` / `onHandoff` are useCallback([]) on the parent and
 *     read latest state via stateRef, so their identities never
 *     change for the lifetime of Support.
 *
 * That stability is what makes the surrounding React.memo work.
 */
/** Live-agent handoff pill. Off while live routing is discontinued — see
 *  the note at `showHandoffPill`. Pairs with LIVE_CONCIERGE_ENABLED server-side. */
const LIVE_HANDOFF_PILL_ENABLED = false;

function ChatComposerInner({ disabled, isPreparingHandoff, onSend, onHandoff, specialistName = 'Tommy', draft = '' }) {
  // `draft`: opening words from a `?topic=` link (src/utils/chatTopic.js),
  // left unsent with the caret at the end for the customer to finish.
  const [text, setText] = useState(draft);
  const textareaRef = useRef(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!draft || !el) return;
    el.focus();
    el.setSelectionRange(draft.length, draft.length);
  }, [draft]);

  // Over the limit, the text stays in the box with a note: never sent cut short.
  const tooLong = text.trim().length > MESSAGE_MAX_CHARS;

  const submit = (e) => {
    if (e) e.preventDefault();
    const content = text.trim();
    if (!content || disabled || tooLong) return;
    onSend(content);
    setText('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const canSend = text.trim().length > 0 && !disabled && !tooLong;

  return (
    <div className="sticky bottom-0 pb-4 pt-2 bg-gradient-to-t from-black via-black/95 to-transparent">
      <form
        onSubmit={submit}
        className="relative flex items-end gap-2 rounded-[26px] bg-white/[0.05] border border-white/[0.09] focus-within:border-white/[0.18] backdrop-blur-md transition-colors px-2.5 py-2 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.6)]"
      >
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={`Message ${specialistName}…`}
          rows={1}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="on"
          autoCapitalize="sentences"
          spellCheck="true"
          className="tommy-textarea flex-1 resize-none bg-transparent text-white placeholder:text-white/35 outline-none border-0 px-3 py-2.5 leading-[1.45] max-h-[160px] disabled:opacity-60"
          aria-label={`Message ${specialistName}`}
        />
        <button
          type="submit"
          disabled={!canSend}
          className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 hover:scale-[1.04] active:scale-95"
          style={{
            background:
              'linear-gradient(135deg, hsl(235, 80%, 30%) 0%, hsl(233, 75%, 23%) 100%)',
            boxShadow: '0 6px 16px -6px rgba(15, 40, 138, 0.7)',
          }}
          aria-label="Send message"
        >
          <Send className="w-4 h-4" strokeWidth={2} />
        </button>
      </form>

      {tooLong ? (
        <p className="mt-2 px-3 text-[13px] text-amber-200/90 font-normal leading-snug" role="alert">
          {MESSAGE_TOO_LONG}
        </p>
      ) : null}

      {/* "Reach a human" lived here, under every single reply. It's gone
          for the same reason as the empty-state number: escalation is
          Stephanie's decision now, and a permanent shortcut to a live
          concierge under every message is the opposite of that. Tommy
          raises it himself when she says a situation is urgent. */}
      <p className="text-center mt-2.5 text-[11px] text-white/30 leading-snug">
        {specialistName} is an AI concierge and may occasionally be wrong. Ask them anything, or check the{' '}
        <Link to="/help" className="underline hover:text-white/55 transition-colors">
          Help Center
        </Link>
        .
      </p>
    </div>
  );
}
ChatComposerInner.propTypes = {
  disabled: PropTypes.bool,
  isPreparingHandoff: PropTypes.bool,
  onSend: PropTypes.func.isRequired,
  onHandoff: PropTypes.func,
  specialistName: PropTypes.string,
  /** Opening words from a `?topic=` link, left unsent. */
  draft: PropTypes.string,
};
const ChatComposer = React.memo(ChatComposerInner);

export default function Support() {
  /**
   * Role-aware routing inside the same /support route:
   *   - Admins land on the Concierge Inbox (review every customer's
   *     chats for QA). They can flip to the chat experience via the
   *     "Chat with Tommy" button.
   *   - Everyone else (including investors / backers / regular users)
   *     gets the customer chat experience, unchanged.
   *
   * Admin detection reads the cached user written by Layout.jsx after
   * Supabase sign-in (role pulled from app_metadata.roles). The admin
   * inbox endpoints re-verify role server-side on every request, so
   * tampering with this localStorage value only changes which UI
   * renders — it never gives access to thread data.
   */
  // Admin status is derived live from the Supabase session below.
  // We start `false` so non-admin is the default until auth resolves
  // — this avoids momentarily flashing the AdminSupportInbox to a
  // signed-out viewer, and avoids the previous bug where a stale
  // localStorage `defera.user` would keep the admin UI rendered for
  // a non-admin user after an in-tab account switch.
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminView, setAdminView] = useState('inbox'); // 'inbox' | 'chat'

  const [messages, setMessages] = useState([]);
  // Opening words for the composer from a `?topic=` link, read once.
  const [composerDraft] = useState(() =>
    (typeof window === 'undefined' ? '' : chatDraftFromSearch(window.location.search)));
  // Which concierge specialist is at the desk for this visit. The opener
  // endpoint assigns it (sticky while a conversation is in flight, rotates
  // between visits); each SSE `done` re-confirms it. The ref mirrors the
  // state so the send path — which reads snapshots, not live state — can
  // stamp the outgoing request without re-rendering.
  const [specialist, setSpecialist] = useState(CONCIERGE_SPECIALISTS[0]);
  const specialistRef = useRef(CONCIERGE_SPECIALISTS[0]);
  const adoptSpecialist = useCallback((raw) => {
    if (!raw?.id) return null;
    const sp = getSpecialist(raw.id);
    specialistRef.current = sp;
    setSpecialist(sp);
    return sp;
  }, []);
  // NOTE: `input` is intentionally NOT held here. The composer subtree
  // (see ChatComposer below) owns its own input state so typing only
  // re-renders the composer, not the entire Support tree (the long
  // list of memoized bubbles, the welcome card,
  // etc.). That isolation is the dominant typing-perf win on mobile.
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [isPreparingHandoff, setIsPreparingHandoff] = useState(false);
  // Authorized by the server (consult triage returned `urgent`), never
  // inferred from what Tommy said. Sticky for the thread once granted.
  const [handoffUnlocked, setHandoffUnlocked] = useState(false);
  // Used by the incoming-CTA effect further down (it has to live below
  // sendMessage — see the note there).
  const location = useLocation();
  const navigate = useNavigate();
  const autoAskFiredRef = useRef(false);
  // A "Continue to booking" card from the desk's live flight search
  // (SSE `flight_handoff`) — held until `done`, then pinned to the
  // reply bubble it belongs to.
  const pendingFlightHandoffRef = useRef(null);
  // Tappable mini flight cards for a search's results (SSE
  // `flight_options`) — same pin-at-done lifecycle as the handoff card.
  const pendingFlightOptionsRef = useRef(null);
  // Bumped by "New chat" to re-arm Tommy's opening greeting.
  const [greetingNonce, setGreetingNonce] = useState(0);

  /**
   * Verified booking context — set after the customer proves ownership
   * via the lookup dialog. Held only in component state (NEVER persisted
   * to localStorage with the chat transcript) so it's wiped on refresh
   * and never leaks across sessions. Shape: { accessToken, summary,
   * expiresAt }.
   */
  const [bookingContext, setBookingContext] = useState(null);

  /**
   * Thread id used by the server to persist this conversation into the
   * admin inbox. Minted on the first user turn (or restored from
   * localStorage on page reload) and reset whenever the user starts a
   * new chat. Held alongside the transcript so refreshing the page
   * keeps the same inbox row.
   */
  const [threadId, setThreadId] = useState(null);

  /**
   * One conversation per customer (server/support-conversation.js). The
   * server's copy loads once we know who this is. `latestSeqRef` is where
   * the live check for team messages picks up; `hasEarlier` says there is
   * history above what's on screen (behind "New chat", or older than the
   * first page).
   */
  const latestSeqRef = useRef(null);
  const isMounted = useIsMounted();
  const [hasEarlier, setHasEarlier] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  // Arrived from a team email or push (/support?thread=…). Signed out, the
  // app's sign-in wall shows on this same URL and lands them here after.
  const [arrivedFromTeamLink] = useState(() => {
    try { return new URLSearchParams(window.location.search).has('thread'); } catch { return false; }
  });

  const scrollRef = useRef(null);
  // Whether the transcript has been positioned once since mount. Gates the
  // one unconditional jump-to-newest — see the auto-scroll effect below.
  const didInitialScrollRef = useRef(false);
  // Whether the reader was at the newest message before the latest change,
  // measured as they scroll. A long new bubble (a team message, the server's
  // copy of the conversation) would otherwise push the bottom out of reach
  // of the after-render distance check and leave them short of it.
  const nearBottomRef = useRef(true);
  const onTranscriptScroll = useCallback((e) => {
    const el = e.currentTarget;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);
  /**
   * Callback ref for the message list.
   *
   * The initial jump-to-newest can't live in the effect alone: an admin lands
   * on the inbox view first, so the transcript finishes loading while this
   * element doesn't exist yet — the effect sees a null ref, and `messages`
   * never changes again to re-fire it once the chat view mounts. Positioning
   * on attach covers that, and the effect covers the reverse order (element
   * present before the transcript arrives). Whichever happens second wins;
   * the ref flag makes sure it only happens once.
   */
  const attachScrollRef = useCallback((node) => {
    scrollRef.current = node;
    if (!node || didInitialScrollRef.current) return;
    if (!stateRef.current?.messages?.length) return;
    didInitialScrollRef.current = true;
    requestAnimationFrame(() => { node.scrollTop = node.scrollHeight; });
  }, []);
  const abortRef = useRef(null);

  /**
   * Latest-state ref read by the stable callbacks (`sendMessage`,
   * `handoffToHuman`) so they can run with empty dep arrays and stay
   * referentially stable across renders. That stability is what lets
   * the ChatComposer subtree (which receives them as props) stay
   * memoized and skip re-rendering when its parent Support re-renders.
   *
   * Why a ref instead of plain closure: every state value sendMessage
   * reads (`messages`, `isStreaming`, `bookingContext`, `threadId`,
   * `authUserId`, `isPreparingHandoff`) would otherwise need to be in
   * its dep array, invalidating the callback identity on every
   * conversational event — which would invalidate the composer's
   * memo and erase the typing-perf win.
   *
   * Populated below (in one block, after all state has been declared)
   * and re-populated on every render so the callbacks always see the
   * current snapshot.
   */
  const stateRef = useRef({});

  /**
   * The authenticated Supabase user id, resolved live from
   * `supabase.auth`. This is the privacy boundary for everything in
   * this component: localStorage keys are partitioned by it (see
   * `storageKeysFor`), and on every change we wipe in-memory state
   * so a previous user's transcript can never bleed into the next
   * sign-in on the same browser.
   *
   * `null` means "auth state still resolving" OR "signed out" — in
   * either case we render an empty conversation and skip persistence.
   * Set explicitly to the uid string once we have one.
   */
  const [authUserId, setAuthUserId] = useState(null);
  // False until getSession() has actually answered. `authUserId` is null both
  // before we know and when the visitor is genuinely signed out, so anything
  // keyed on identity has to wait for this or it fires twice — once for
  // "unknown", again for the real id. That double-fire is what put a random
  // specialist in the header and a DIFFERENT one on the message bubble.
  const [authReady, setAuthReady] = useState(false);
  // Track the previously-seen user id so we can detect *changes*
  // (not just initial set) and respond by clearing state.
  const previousAuthUserRef = useRef(null);

  // Resolve the current user and subscribe to auth state changes so we
  // react LIVE to sign-in / sign-out / user-switch — not just on a hard
  // refresh. We scrub the legacy global storage keys on first mount of
  // any authenticated session to make sure pre-fix data can't leak.
  useEffect(() => {
    let cancelled = false;

    // Scrub legacy global keys on first mount — they were the source
    // of the cross-user leak fixed in this commit. Safe to remove
    // unconditionally; per-user namespaced keys live elsewhere.
    try {
      for (const k of LEGACY_STORAGE_KEYS) localStorage.removeItem(k);
    } catch { /* quota */ }

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        const user = data?.session?.user || null;
        setAuthUserId(user?.id || null);
        setIsAdmin(isSessionAdmin(user));
        setAuthReady(true);
      } catch {
        if (!cancelled) {
          setAuthUserId(null);
          setIsAdmin(false);
          setAuthReady(true);
        }
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      // Any auth event — sign in, sign out, token refresh with a
      // different user — flows through here. We let the user-change
      // effect below handle wiping in-memory state; this just keeps
      // `authUserId` AND `isAdmin` in lockstep with the live session.
      // Deriving `isAdmin` here (from session.user.app_metadata.roles,
      // which is the same field the server uses) is what fixes the
      // bug where signing out of an admin and signing in as a regular
      // customer in the SAME tab kept the AdminSupportInbox UI
      // rendered for the new user (who would then get a 403 from the
      // server because the admin guard correctly rejected them).
      const user = session?.user || null;
      setAuthUserId(user?.id || null);
      setIsAdmin(isSessionAdmin(user));
    });

    return () => {
      cancelled = true;
      try { sub?.subscription?.unsubscribe?.(); } catch { /* ignore */ }
    };
  }, []);

  // Restore the prior conversation + threadId from THIS user's private
  // localStorage namespace whenever the signed-in user changes. The
  // user-change detection is what closes the cross-user leak: if
  // someone signs out and a different user signs in, we immediately
  // wipe in-memory state instead of continuing to render the previous
  // user's transcript.
  useEffect(() => {
    const prev = previousAuthUserRef.current;
    previousAuthUserRef.current = authUserId;

    // Auth state still resolving or signed out → render empty and
    // skip persistence entirely. We do NOT load from anywhere here.
    if (!authUserId) {
      // Only wipe if there was previously a user — avoids clobbering
      // the brief initial render before getSession resolves.
      if (prev) {
        setMessages([]);
        setThreadId(null);
        setBookingContext(null);
      }
      return;
    }

    // First time we see *any* uid this mount, OR we see a *different*
    // uid than before → reset in-memory state and load from the new
    // user's namespace. Without this guard, a sign-out → sign-in as a
    // different user would keep the prior transcript on screen.
    if (prev !== authUserId) {
      // A different person's transcript is about to load — let it position
      // itself at its newest message, same as a fresh mount would.
      didInitialScrollRef.current = false;
      setMessages([]);
      setThreadId(null);
      setBookingContext(null);
    }

    const keys = storageKeysFor(authUserId);
    if (!keys) return;
    try {
      const raw = localStorage.getItem(keys.transcript);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          setMessages(parsed.slice(-MAX_HISTORY));
        }
      }
      const savedThreadId = localStorage.getItem(keys.threadId);
      if (savedThreadId) setThreadId(savedThreadId);
    } catch {
      // Corrupt cache — fine to start fresh
    }
  }, [authUserId]);

  /**
   * Tommy says hello first.
   *
   * A support page that sits silent until you type is a form. A concierge
   * who greets you when you walk up is a person — this is the cheapest
   * realism win in the whole chat and it costs one query.
   *
   * The server decides the wording: a stranger gets the self-introduction
   * ("Hi — I'm Tommy, the concierge here at Defera"), someone who's chatted
   * before just gets a warm hello. Introducing himself by name to a regular
   * every single time is worse than not greeting at all — it's the tell
   * that nobody's home.
   *
   * Two races to survive, both handled by the functional update below:
   *   - **Transcript restore.** The effect above may rehydrate a prior
   *     conversation from localStorage while this fetch is in flight.
   *     Checking `prev.length === 0` at apply time (not at fetch time)
   *     means a restored transcript silently wins and we never staple a
   *     greeting onto the middle of an existing chat.
   *   - **User switch.** `cancelled` plus the identity re-check stop a
   *     greeting resolved for the previous user from landing in the next
   *     user's window after a sign-out/sign-in.
   */
  const deskRequestedForRef = useRef(undefined);
  // **Who is at the desk.** The concierge no longer speaks first — the
  // customer opens the conversation and the specialist introduces themselves
  // in their first reply (the `ack` bubble, see buildAckLine server-side).
  // This effect therefore fetches an IDENTITY, not a message: the header
  // needs a name and a face before anyone types.
  //
  // Gated on `authReady`. It used to run on `authUserId` alone, which is null
  // both before auth resolves and when signed out — so it fired once as
  // "anonymous" and again as the real user, each time getting an independently
  // random specialist from the server. The first response stamped the greeting
  // bubble; the second overwrote the header state. That is precisely how the
  // header came to say "Kai" over a message signed "Isabella".
  useEffect(() => {
    if (!authReady) return;
    if (deskRequestedForRef.current === authUserId) return;
    deskRequestedForRef.current = authUserId;

    let cancelled = false;
    (async () => {
      try {
        let authHeader = null;
        try {
          const { data } = await supabase.auth.getSession();
          if (data?.session?.access_token) authHeader = `Bearer ${data.session.access_token}`;
        } catch { /* anonymous is fine */ }

        // The stored threadId lets the server keep the same specialist for
        // an in-flight conversation (and rotate an idle one).
        let storedThreadId = null;
        try {
          const keys = storageKeysFor(authUserId);
          if (keys) storedThreadId = localStorage.getItem(keys.threadId);
        } catch { /* anonymous / quota */ }

        const res = await fetch(
          `/api/support-chat/opener${storedThreadId ? `?threadId=${encodeURIComponent(storedThreadId)}` : ''}`,
          { headers: authHeader ? { Authorization: authHeader } : {} },
        );
        if (cancelled || deskRequestedForRef.current !== authUserId) return;
        if (res.ok) {
          const data = await res.json();
          adoptSpecialist(data?.specialist);
        }
      } catch {
        // Falling back to the default specialist is a fine degradation —
        // the composer still works. Never surface this as an error.
      }
    })();

    return () => { cancelled = true; };
    // greetingNonce re-arms this on "New chat" — see handleReset.
  }, [authReady, authUserId, greetingNonce]);

  // Persist conversation into the signed-in user's private namespace.
  // No userId → no persistence (anonymous chats live only in memory).
  useEffect(() => {
    if (!authUserId) return;
    const keys = storageKeysFor(authUserId);
    if (!keys) return;
    try {
      // A transcript with no customer turn in it is just Tommy's opening
      // greeting, which we deliberately do NOT cache: the greeting is
      // computed per visit (stranger vs regular) and a stale cached
      // "Hi — I'm Tommy" would keep re-introducing him to someone he
      // already knows, which is the exact robot tell the opener exists to
      // avoid. Let it be re-fetched fresh on every visit instead.
      if (!messages.some((m) => m.role === 'user')) {
        localStorage.removeItem(keys.transcript);
      } else {
        localStorage.setItem(keys.transcript, JSON.stringify(messages.slice(-MAX_HISTORY)));
      }
    } catch {
      // Quota exhausted — fine to silently drop
    }
  }, [messages, authUserId]);

  const adoptThreadId = useCallback((id) => {
    if (!id) return;
    setThreadId(id);
    const keys = storageKeysFor(stateRef.current.authUserId);
    if (keys) {
      try { localStorage.setItem(keys.threadId, id); } catch { /* quota */ }
    }
  }, []);

  // Admins on the QA inbox aren't looking at their own chat.
  const chatVisible = !(isAdmin && adminView === 'inbox');

  /**
   * The server's copy of the conversation: every device shows the same
   * chat, with the team's messages in it, and mergeConversation keeps this
   * device's flight cards on their bubbles. A failed load leaves this
   * device's copy on screen (and the live check off until a load works).
   */
  const loadServerConversation = useCallback(async () => {
    const userId = stateRef.current.authUserId;
    if (!userId) return;
    const authHeader = await currentAuthHeader();
    if (!authHeader || !isMounted()) return;
    try {
      const res = await fetch('/api/support-chat/conversation', { headers: { Authorization: authHeader } });
      const data = res.ok ? await res.json() : null;
      if (!isMounted() || !data?.threadId || stateRef.current.authUserId !== userId) return;
      adoptThreadId(data.threadId);
      if (data.latestSeq != null) latestSeqRef.current = Math.max(latestSeqRef.current ?? 0, data.latestSeq);
      setHasEarlier(Boolean(data.hasEarlier));
      const remote = (data.messages || []).map(toBubble);
      setMessages((prev) => {
        // A turn already streaming keeps its transcript and only gains team messages.
        const next = stateRef.current.isStreaming
          ? appendTeamBubbles(prev, remote)
          : mergeConversation(prev, remote, { contextResetAt: data.contextResetAt });
        // A reader at the newest message lands at the new newest message:
        // an instant jump, as on first paint, not a long smooth scroll.
        if (next !== prev && nearBottomRef.current) didInitialScrollRef.current = false;
        return next;
      });
    } catch {
      // Offline or a blip: this device's copy stands.
    }
  }, [adoptThreadId, isMounted]);

  // Once we know who this is (after the localStorage restore above).
  useEffect(() => {
    if (!authReady || !authUserId || !chatVisible) return;
    loadServerConversation();
  }, [authReady, authUserId, chatVisible, loadServerConversation]);

  /** The live check: a message from the team shows up while the chat is open. */
  const pollTeamMessages = useCallback(async () => {
    const s = stateRef.current;
    if (!s.authUserId || s.isStreaming || latestSeqRef.current == null) return;
    const authHeader = await currentAuthHeader();
    if (!authHeader || !isMounted()) return;
    try {
      const res = await fetch(
        `/api/support-chat/conversation?after=${encodeURIComponent(latestSeqRef.current)}&teamOnly=1`,
        { headers: { Authorization: authHeader } },
      );
      const data = res.ok ? await res.json() : null;
      if (!isMounted() || !data?.ok || stateRef.current.authUserId !== s.authUserId) return;
      if (data.latestSeq != null) latestSeqRef.current = Math.max(latestSeqRef.current ?? 0, data.latestSeq);
      const fresh = (data.messages || []).map(toBubble);
      if (fresh.length) setMessages((prev) => appendTeamBubbles(prev, fresh));
    } catch {
      // The next check will try again.
    }
  }, [isMounted]);

  // Live (migration 203): the moment the team posts in this conversation,
  // the database signals it and the chat fetches the message at once. The
  // timed check below stays as the fallback.
  useEffect(() => {
    if (!authReady || !authUserId || !chatVisible || !threadId) return undefined;
    return listenForSignals(`thread:${threadId}`, (payload) => {
      if (payload.role === 'team') pollTeamMessages();
    });
  }, [authReady, authUserId, chatVisible, threadId, pollTeamMessages]);

  // Every 20 seconds while the chat is on screen, and right away when the
  // customer comes back to it (from the email, another app, another tab).
  useEffect(() => {
    if (!authReady || !authUserId || !chatVisible) return undefined;
    const check = () => {
      if (document.visibilityState === 'visible') pollTeamMessages();
    };
    const timer = setInterval(check, TEAM_POLL_MS);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, [authReady, authUserId, chatVisible, pollTeamMessages]);

  // The team link has done its job once they're signed in: drop it from the
  // address so a refresh or a shared URL doesn't carry it.
  useEffect(() => {
    if (!arrivedFromTeamLink || !authUserId) return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('thread')) {
        url.searchParams.delete('thread');
        window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
      }
    } catch { /* ignore */ }
  }, [arrivedFromTeamLink, authUserId]);

  /** "Show earlier messages": page back through the conversation, keeping the reader's place. */
  const loadEarlier = useCallback(async () => {
    if (loadingEarlier) return;
    const before = oldestSeq(stateRef.current.messages)
      ?? (latestSeqRef.current != null ? latestSeqRef.current + 1 : null);
    if (before == null) return;
    setLoadingEarlier(true);
    try {
      const authHeader = await currentAuthHeader();
      if (!authHeader || !isMounted()) return;
      const res = await fetch(`/api/support-chat/conversation?before=${encodeURIComponent(before)}`, {
        headers: { Authorization: authHeader },
      });
      const data = res.ok ? await res.json() : null;
      if (!isMounted() || !data?.ok) return;
      setHasEarlier(Boolean(data.hasEarlier));
      const older = (data.messages || []).map(toBubble);
      const el = scrollRef.current;
      const fromBottom = el ? el.scrollHeight - el.scrollTop : null;
      setMessages((prev) => prependEarlier(prev, older));
      if (el && fromBottom != null) {
        requestAnimationFrame(() => { el.scrollTop = el.scrollHeight - fromBottom; });
      }
    } catch {
      // Tap again.
    } finally {
      if (isMounted()) setLoadingEarlier(false);
    }
  }, [loadingEarlier, isMounted]);

  // Auto-scroll to bottom whenever messages change or while streaming.
  // Three rules:
  //   1. If the customer just sent a message (last role === 'user'),
  //      always scroll — they expect to see their own bubble.
  //   2. If they're already near the bottom (within 120px), follow
  //      the new content / streamed tokens down so the most recent
  //      reply stays in view.
  //   3. If they scrolled up to re-read an earlier message, respect
  //      that and do NOT yank them back down mid-read.
  // Use `behavior: 'auto'` while streaming (avoids stacked smooth
  // animations on every token) and `'smooth'` for message-level
  // transitions where one nice animation reads better than instant.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) return;
    const last = messages[messages.length - 1];
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const STICK_THRESHOLD = 120;

    // First paint of a restored conversation: jump to the newest message,
    // no animation, no distance check.
    //
    // The threshold below asks "is the reader already at the bottom?" so we
    // never yank someone who has deliberately scrolled up to re-read. On
    // mount nobody has scrolled anywhere — scrollTop is 0 because nothing
    // has positioned it yet — and a long transcript therefore failed the
    // check and opened at the very first message, which is the wrong end of
    // a chat. (Invisible until the flex `min-h-0` fix above gave this
    // element a scrollable range at all.)
    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
      return;
    }

    const shouldStick =
      last?.role === 'user' || nearBottomRef.current || distanceFromBottom < STICK_THRESHOLD;
    if (!shouldStick) return;
    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: isStreaming ? 'auto' : 'smooth',
      });
    });
  }, [messages, isStreaming]);

  // Textarea auto-grow is handled in CSS now via `field-sizing: content`
  // (see the `.tommy-textarea` rule below). Doing this in JS caused
  // typing lag on mobile: every keystroke set `style.height = 'auto'`
  // → read `scrollHeight` → set the new height, forcing a synchronous
  // layout flush AND re-rasterizing every overlapping `backdrop-blur`
  // surface (the composer pill and every Tommy bubble). CSS does the
  // same thing on the compositor thread, off the main thread.

  // Cancel in-flight stream when the user navigates away
  useEffect(() => {
    return () => {
      try { abortRef.current?.abort(); } catch { /* ignore */ }
    };
  }, []);

  // Re-populate the stateRef on every render so the stable callbacks
  // below always read fresh state. Cheap (a few property assignments).
  stateRef.current.messages = messages;
  stateRef.current.isStreaming = isStreaming;
  stateRef.current.isPreparingHandoff = isPreparingHandoff;
  stateRef.current.bookingContext = bookingContext;
  stateRef.current.threadId = threadId;
  stateRef.current.authUserId = authUserId;

  const sendMessage = useCallback(async (text) => {
    // ALL state reads go through stateRef so this callback can stay
    // referentially stable (empty dep array) — see stateRef docs above.
    const s = stateRef.current;
    const content = String(text || '').trim();
    if (!content || s.isStreaming) return;
    if (content.length > MESSAGE_MAX_CHARS) {
      setError(MESSAGE_TOO_LONG);
      return;
    }

    // Snapshot the transcript NOW, before we touch state or await anything.
    //
    // `stateRef.current` is re-populated on every render, and this function
    // awaits (`supabase.auth.getSession()`) after calling setMessages. React
    // re-renders during that await and reassigns `stateRef.current.messages`
    // — the same object `s` points at — so reading `s.messages` down at the
    // fetch returned the ALREADY-UPDATED array, the one containing the
    // message we're about to send plus its empty placeholder. The request
    // body then went out as [user, "", user]: the customer's message
    // duplicated on every single turn.
    //
    // It failed quietly — the model just saw the question twice and
    // answered it once — so it survived until the ack, which counts user
    // turns to detect the first substantive message, started reading 2
    // where there was 1 and silently never fired.
    const priorMessages = [...(s.messages || [])];

    setError(null);

    // Mint a thread id the first time the user actually sends something
    // in this chat. Persisted to the SIGNED-IN user's private localStorage
    // namespace so a page refresh keeps the same server-side inbox row
    // — and so it can't leak into another user's session on the same
    // browser. Anonymous chats are minted in-memory only (no persistence).
    let activeThreadId = s.threadId;
    if (!activeThreadId) {
      activeThreadId = newThreadId();
      setThreadId(activeThreadId);
      const keys = storageKeysFor(s.authUserId);
      if (keys) {
        try { localStorage.setItem(keys.threadId, activeThreadId); } catch { /* quota */ }
      }
    }

    const userMsg = { role: 'user', content, timestamp: Date.now() };
    const assistantPlaceholder = { role: 'assistant', content: '', timestamp: Date.now() };
    setMessages((prev) => [...prev, userMsg, assistantPlaceholder]);
    setIsStreaming(true);

    // Pull auth header so Tommy can personalize when the user is logged in
    let authHeader = null;
    try {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.access_token) authHeader = `Bearer ${data.session.access_token}`;
    } catch {
      // Anonymous is fine
    }

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch('/api/support-chat', {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(authHeader ? { Authorization: authHeader } : {}),
        },
        body: JSON.stringify({
          // priorMessages, NOT s.messages — see the snapshot note above.
          // Team messages stay out: the desk reads those from the server.
          messages: historyForDesk([...priorMessages, userMsg]),
          threadId: activeThreadId,
          specialistId: specialistRef.current?.id,
          // The device already knows where the customer is. Sending it beats
          // every inference, and without it the desk used to narrate UTC —
          // greeting someone in California with "good morning" at 7pm.
          clientTimeZone: (() => {
            try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
          })(),
          ...(s.bookingContext?.accessToken
            ? { bookingAccessToken: s.bookingContext.accessToken }
            : {}),
        }),
      });

      if (!res.ok || !res.body) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `Support chat is unavailable (status ${res.status}).`);
      }

      // SSE parser. We accumulate token events into the assistant placeholder.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      /**
       * `ack` and `stall` each land as their OWN finished bubble, with a
       * fresh empty placeholder opened underneath for the answer to stream
       * into.
       *
       * That two-bubble shape is the entire realism mechanic. A real
       * person sends "one sec, let me pull that up" as a message, and then
       * sends the answer as a second message — they do not compose a
       * single message containing both. Folding the ack into the top of
       * the reply reads as a stalling paragraph; splitting it reads as a
       * person.
       *
       * The placeholder renders TypingDots (see TommyBubble), so the beat
       * between the two bubbles shows Tommy visibly working.
       */
      const finalizeBubbleAndOpenNext = (text, kind) => {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          // Fill the waiting placeholder rather than appending, so the
          // dots become the ack in place instead of stacking an extra
          // empty bubble.
          if (last?.role === 'assistant' && !last.content) {
            next[next.length - 1] = { ...last, content: text, kind };
          } else {
            next.push({ role: 'assistant', content: text, kind, timestamp: Date.now() });
          }
          // Open the next placeholder — subsequent tokens target the last
          // message, so this is what the answer streams into.
          next.push({ role: 'assistant', content: '', timestamp: Date.now() });
          return next;
        });
      };

      const dispatch = (event, payload) => {
        if (event === 'token') {
          const chunk = payload; // raw string
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'assistant') {
              next[next.length - 1] = { ...last, content: (last.content || '') + chunk };
            }
            return next;
          });
        } else if (event === 'ack' || event === 'stall') {
          let text = '';
          try {
            text = JSON.parse(payload)?.text || '';
          } catch { /* ignore */ }
          if (text) finalizeBubbleAndOpenNext(text, event);
        } else if (event === 'thread') {
          // The customer's one conversation (signed in): every turn lands
          // there whatever id this device had, so adopt it.
          try {
            const id = JSON.parse(payload)?.id;
            if (id) {
              adoptThreadId(id);
              // No server copy loaded yet (a first chat, or the load
              // failed): load it now, which also starts the live check.
              if (latestSeqRef.current == null && stateRef.current.authUserId) loadServerConversation();
            }
          } catch { /* ignore */ }
        } else if (event === 'flight_handoff') {
          // The desk searched live flights and the customer confirmed an
          // itinerary — this payload is the ready-to-book card. Pinned to
          // the reply bubble at `done` so it never lands mid-stream.
          try {
            pendingFlightHandoffRef.current = JSON.parse(payload);
          } catch { /* ignore */ }
        } else if (event === 'flight_options') {
          // Search results as tappable mini cards (the agent's reply text
          // stays short — the cards carry the details).
          try {
            pendingFlightOptionsRef.current = JSON.parse(payload);
          } catch { /* ignore */ }
        } else if (event === 'done') {
          // The server's triage verdict. Sticky: once Stephanie has ruled
          // a thread urgent, the customer keeps the escape hatch for the
          // rest of it — someone in a bad enough moment to warrant a human
          // shouldn't have to re-earn one on the next turn.
          try {
            const done = JSON.parse(payload);
            if (done?.handoffUnlocked) setHandoffUnlocked(true);
            // Pin the reply to whoever actually spoke it, and update the
            // header if the desk rotated server-side.
            const sp = adoptSpecialist(done?.specialist);
            if (sp) {
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last?.role === 'assistant' && last.content) {
                  next[next.length - 1] = { ...last, specialist: sp };
                }
                return next;
              });
            }
          } catch { /* ignore */ }

          // Pin pending flight cards (option list and/or booking-handoff)
          // to the reply they belong to — the last assistant bubble with
          // content. They then persist with the transcript like any other
          // message field.
          if (pendingFlightHandoffRef.current || pendingFlightOptionsRef.current) {
            const handoff = pendingFlightHandoffRef.current;
            const flightOptions = pendingFlightOptionsRef.current;
            pendingFlightHandoffRef.current = null;
            pendingFlightOptionsRef.current = null;
            setMessages((prev) => {
              const next = [...prev];
              for (let i = next.length - 1; i >= 0; i--) {
                if (next[i].role === 'assistant' && next[i].content) {
                  next[i] = {
                    ...next[i],
                    ...(handoff ? { handoff } : {}),
                    ...(flightOptions ? { flightOptions } : {}),
                  };
                  break;
                }
              }
              return next;
            });
          }

          // Drop a trailing placeholder that never received any tokens.
          // Normally impossible, but an ack/stall opens a fresh bubble
          // *before* the answer streams — so if that second pass comes
          // back empty (model returned nothing, consult produced no
          // reply), the placeholder would sit there animating typing dots
          // forever, with `isStreaming` already false. An abandoned bubble
          // that types into the void is worse than no bubble.
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.content) return prev.slice(0, -1);
            return prev;
          });
        } else if (event === 'error') {
          let msg = 'We had trouble responding. Please try again in a moment.';
          let code = null;
          try {
            const parsed = JSON.parse(payload);
            if (parsed?.message) msg = parsed.message;
            if (parsed?.code) code = parsed.code;
          } catch { /* ignore */ }
          setError(msg);
          // If the booking session expired mid-conversation, clear it so
          // the customer can re-link cleanly via "Pull up my trip" again.
          if (code === 'booking_token_expired') {
            setBookingContext(null);
          }
        }
      };

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) { streamDone = true; break; }
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are delimited by a blank line
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let event = 'message';
          const dataLines = [];
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
          }
          if (dataLines.length) dispatch(event, dataLines.join('\n'));
        }
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Support chat failed:', err);
      setError(err?.message || `${specialistRef.current?.name || 'Our concierge'} had trouble responding. Please try again in a moment.`);
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === 'assistant' && !last.content) {
          // Drop the empty placeholder so the UI doesn't show a dead bubble
          next.pop();
        }
        return next;
      });
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, []); // intentionally stable — see stateRef.

  /**
   * A tap on a mini flight card is the same as replying with that option —
   * it goes through sendMessage as a normal customer turn, so the agent's
   * tool flow (fetch returns / propose handoff) is identical for taps and
   * typed replies. Stable identity keeps the memoized bubbles quiet.
   */
  const handlePickFlightOption = useCallback((item, options) => {
    const kindWord = options?.kind === 'return' ? 'return option' : 'option';
    sendMessage(`Let's do ${kindWord} ${item.option} — ${item.airline}, departing ${item.departTime}.`);
  }, [sendMessage]);

  /**
   * Arriving from a CTA elsewhere in the app.
   *
   * Every "contact us" button in Defera — request a flight change, add
   * baggage, redeem miles, checkout trouble — used to open the customer's
   * SMS app with a pre-filled body. They now route here instead, carrying
   * that same body as `?ask=`, and we send it as the customer's opening
   * turn. From their side the flow is identical to the old one (tap the
   * button, the message is already written) except Tommy answers instead
   * of a human's phone buzzing.
   *
   * We auto-send rather than pre-fill the composer because the tap WAS the
   * intent — they clicked "Request cabin upgrade"; making them press send
   * on a sentence we wrote for them is a pointless extra step.
   *
   * The param is stripped from the URL immediately (`replace: true`) so a
   * refresh or a back-navigation doesn't fire the same message again.
   *
   * MUST stay below `sendMessage`. It lived above the declaration and
   * crashed the whole page on mount — `const` bindings sit in the temporal
   * dead zone until initialized, and a dep array is evaluated during
   * render, so naming `sendMessage` up there threw "Cannot access
   * 'sendMessage' before initialization" before Support could paint. The
   * build was perfectly happy with it; only loading the page found it.
   */
  useEffect(() => {
    if (autoAskFiredRef.current) return;
    const params = new URLSearchParams(location.search);
    const ask = params.get(TOMMY_ASK_PARAM);
    if (!ask) return;

    autoAskFiredRef.current = true;
    // Strip before sending — sendMessage is async and we don't want a
    // re-render mid-flight to re-read a param we've already consumed.
    navigate('/support', { replace: true });

    // The trip, when there is one, is named inside `ask` itself (see
    // tommyIntentForBooking) rather than auto-linked: linking needs a
    // booking *id* and CTAs only have the human-readable reference.
    // Tommy has the customer's trips in his account context anyway, and
    // "Pull up my trip" is one tap if he needs the full schedule.

    // Let the greeting land first so the customer's message doesn't
    // appear above Tommy saying hello.
    const t = setTimeout(() => sendMessage(ask), 350);
    return () => clearTimeout(t);
    // sendMessage is referentially stable (useCallback([]) reading via
    // stateRef), so this runs once for the incoming URL and never again.
  }, [location.search, navigate, sendMessage]);

  const handleReset = () => {
    try { abortRef.current?.abort(); } catch { /* ignore */ }
    setMessages([]);
    setError(null);
    setIsStreaming(false);
    // New chat → re-resolve who's at the desk, so a returning customer can
    // get a different specialist the way they would at a real front desk.
    // The effect fires once per identity per mount, so the ref has to be
    // re-armed for it to run again. It assigns an identity only — the
    // introduction itself rides on the first reply.
    deskRequestedForRef.current = undefined;
    // Clearing the ref alone can't re-run the effect — its deps haven't
    // changed. The nonce is what actually re-triggers it.
    setGreetingNonce((n) => n + 1);
    // A fresh thread means a fresh triage. Whatever Stephanie decided
    // about the last conversation must not silently authorize a live
    // concierge in this one.
    setHandoffUnlocked(false);
    // New chat → drop any active booking session. The customer can
    // re-link via "Pull up my trip" if they want trip-specific help in
    // the new conversation.
    setBookingContext(null);
    // Signed in, "New chat" is a fresh start in the SAME conversation (one
    // per customer): the server notes where it was tapped, so the desk
    // starts fresh from here and the history stays a tap away. Signed out,
    // the next message mints a new thread id.
    if (authUserId) {
      if (messages.length && latestSeqRef.current != null) setHasEarlier(true);
      (async () => {
        const authHeader = await currentAuthHeader();
        if (!authHeader) return;
        try {
          await fetch('/api/support-chat/new-topic', { method: 'POST', headers: { Authorization: authHeader } });
        } catch { /* the screen is clear either way */ }
      })();
    } else {
      setThreadId(null);
    }
  };

  /**
   * Hand off the conversation to a real human via SMS. We ask the server
   * to produce a one-line, customer-voice context summary, then open the
   * native SMS composer with the concierge number pre-selected and the
   * body pre-filled. If the customer hasn't said anything yet, we skip the
   * summary call entirely and fall back to a plain greeting (same template
   * Contact.jsx uses).
   *
   * Critical: this function must call `window.location.href = ...` (or
   * `window.open(...)`) *synchronously* inside the click handler on iOS,
   * otherwise Safari blocks the navigation. Since we have to fetch the
   * summary first, we open a placeholder window synchronously and update
   * its location once the fetch resolves — this is the standard pattern
   * for async navigation on iOS.
   */
  const handoffToHuman = useCallback(async () => {
    // ALL state reads go through stateRef so this callback can stay
    // referentially stable (empty dep array) — see stateRef docs.
    const s = stateRef.current;
    if (s.isPreparingHandoff) return;

    // Anonymous user with no conversation → just open SMS with the
    // generic Contact.jsx greeting. No need to spin up the summarizer.
    if (!s.messages?.length) {
      window.location.href = buildSmsUrl('Hi, I have a question about Defera.');
      return;
    }

    setIsPreparingHandoff(true);
    setError(null);

    let authHeader = null;
    let userFirstName = null;
    try {
      const { data } = await supabase.auth.getSession();
      const session = data?.session;
      if (session?.access_token) authHeader = `Bearer ${session.access_token}`;
      const meta = session?.user?.user_metadata || {};
      userFirstName = meta.first_name || meta.firstName || null;
    } catch {
      // Anonymous handoff is fine
    }

    let summary = null;
    let source = null;
    try {
      const res = await fetch('/api/support-chat/handoff-summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authHeader ? { Authorization: authHeader } : {}),
        },
        body: JSON.stringify({
          messages: historyForDesk(s.messages),
          // Tells the server to mark this thread as handed_off in the
          // admin inbox so escalations bubble to the top of the list.
          ...(s.threadId ? { threadId: s.threadId } : {}),
          ...(s.bookingContext?.accessToken
            ? { bookingAccessToken: s.bookingContext.accessToken }
            : {}),
        }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data?.summary) summary = String(data.summary);
        if (data?.source) source = String(data.source);
      }
    } catch {
      // Network failure → fall through with summary = null (still useful)
    } finally {
      setIsPreparingHandoff(false);
    }

    // Verbatim SMS bodies (e.g. the "release my confirmation number"
    // request, which mirrors the Trips page Trip Completion wording)
    // already include their own greeting. Use them as-is — wrapping
    // them would produce a double-greeting that reads weirdly.
    if (summary && source && source.startsWith('verbatim_')) {
      window.location.href = buildSmsUrl(summary);
      return;
    }

    // If the summarizer didn't return anything, fall back to the last
    // thing the customer actually said so the agent still has *some*
    // context — better than an empty greeting.
    if (!summary) {
      const lastUserMsg = [...s.messages].reverse().find((m) => m.role === 'user');
      if (lastUserMsg?.content) {
        summary = lastUserMsg.content.length > 220
          ? `${lastUserMsg.content.slice(0, 217)}…`
          : lastUserMsg.content;
      }
    }

    const body = composeHandoffSms({ summary, userFirstName, specialistName: specialistRef.current?.name });
    window.location.href = buildSmsUrl(body);
  }, []); // intentionally stable — see stateRef.

  /**
   * Focus a single trip, from the `/support?bookingId=…` deep link on a
   * trip's page. (The picker dialog that also called this is gone — the
   * concierge preloads every trip, so there was nothing to pick.)
   * Stashes the access token + redacted summary in component state and
   * inserts a single system-flavored message into the chat so the
   * customer SEES that Tommy now has the trip pulled up. The actual
   * sensitive details never appear in the chat transcript — only the
   * fact that a trip is linked.
   */
  const handleVerified = useCallback(({ accessToken, summary, expiresInSeconds }) => {
    const expiresAt = Date.now() + (Number(expiresInSeconds) || 30 * 60) * 1000;
    setBookingContext({ accessToken, summary, expiresAt });
    setError(null);

    const ref = summary?.booking_reference ? `**${summary.booking_reference}**` : 'this trip';
    const statusBit = summary?.status_label ? ` (${summary.status_label})` : '';
    const noteContent = `Got it — I'm now looking at ${ref}${statusBit}. Ask me anything about this trip and I'll answer from the details on file.`;
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: noteContent, timestamp: Date.now(), system: true },
    ]);
  }, []);

  const clearBookingContext = useCallback(() => {
    setBookingContext(null);
  }, []);

  /**
   * Deep-link: when a customer taps the chat button on a trip in the
   * dashboard, they land here as `/support?bookingId=<uuid>`. Auto-attach
   * that trip to the conversation so they skip the "Pull up my trip"
   * step. We then strip the param from the URL so refreshes don't
   * re-fetch and so the URL stays clean if they share/copy it.
   *
   * Skip for admins still on the Inbox view — they aren't seeing the
   * chat UI yet, and we don't want to silently hijack the URL before
   * the chat renders.
   */
  useEffect(() => {
    if (isAdmin && adminView !== 'chat') return;
    if (typeof window === 'undefined') return;
    let cancelled = false;
    (async () => {
      let bookingId = null;
      try {
        const params = new URLSearchParams(window.location.search);
        bookingId = params.get('bookingId');
      } catch { /* malformed query */ }
      if (!bookingId) return;

      let authHeader = null;
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session?.access_token) authHeader = `Bearer ${data.session.access_token}`;
      } catch { /* ignore */ }
      if (!authHeader || cancelled) return;

      try {
        const res = await fetch('/api/support-chat/load-my-booking', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: authHeader,
          },
          body: JSON.stringify({ bookingId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data?.ok) {
          handleVerified({
            accessToken: data.accessToken,
            summary: data.summary,
            expiresInSeconds: data.expiresInSeconds,
          });
        }
      } catch { /* network errors are best-effort */ }
      finally {
        // Always strip the param so refresh / back nav doesn't replay this.
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.has('bookingId') || url.searchParams.has('topic')) {
            url.searchParams.delete('bookingId');
            url.searchParams.delete('topic');
            window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
          }
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, adminView, handleVerified]);

  // The welcome affordances (quick prompts, "Pull up my trip") stay up
  // until the customer actually says something — NOT until `messages` is
  // non-empty. Tommy's opening greeting is itself a message now, so the
  // old `messages.length === 0` test would have torn the quick prompts
  // away the instant he said hello.
  const showWelcome = !messages.some((m) => m.role === 'user');
  const hasConversation = messages.length > 0;

  /**
   * LIVE ROUTING IS DISCONTINUED (2026-08-20) — the "Text a human" pill
   * never renders.
   *
   * It opened an SMS thread to the staffed line with an AI-prepared summary.
   * That route is retired: the concierge now writes every request up for the
   * team instead, with a turnaround set by urgency (a few hours if genuinely
   * time-critical, 24 hours otherwise). The desk prompts were updated in the
   * same pass, so nothing in chat points at a button that isn't here.
   *
   * The unlock plumbing (`handoffUnlocked`, set by the server's `done` event)
   * is deliberately left intact rather than ripped out — the decision is
   * "for now", and restoring the pill is a one-line change here plus
   * LIVE_CONCIERGE_ENABLED=true on the server. The SMS number itself stays
   * reachable and stays printed on trip statements; we simply stop pointing
   * customers at it.
   */
  const showHandoffPill = LIVE_HANDOFF_PILL_ENABLED ? handoffUnlocked : false;

  // Admins default to the Concierge Inbox for QA. The "Chat with Defera
  // Support" button there flips `adminView` to 'chat' and the regular chat
  // experience renders below. From chat mode admins can return via the
  // "Inbox" affordance we inject into the chat header.
  if (isAdmin && adminView === 'inbox') {
    return <AdminSupportInbox onSwitchToChat={() => setAdminView('chat')} />;
  }

  return (
    <>
      <Helmet>
        <title>Defera Support · Chat with our concierge team</title>
        <meta
          name="description"
          content="Chat with Defera Support, our AI concierge team. Get instant answers on Defera Zero, installments, bookings, payments, and your trips — any time, day or night."
        />
        {/* Intentionally NOT overriding the viewport here. The app's
            global meta in index.html omits viewport-fit=cover so the
            Layout's fixed nav pill sits safely beneath the iOS status
            bar on every page. Setting viewport-fit=cover on this page
            would let the page paint under the notch and the fixed nav
            would slide under the iOS time/battery icons.
            Auto-zoom-on-focus is still handled — the chat input is
            locked to 16px on mobile via the .tommy-textarea rule
            below, which is the canonical fix. */}
      </Helmet>

      <style>{`
        @keyframes tommy-blink {
          0%, 50% { opacity: 1; }
          50.01%, 100% { opacity: 0; }
        }
        .tommy-textarea::placeholder { color: rgba(255,255,255,0.36); }
        /* iOS Safari auto-zooms inputs whose computed font-size is < 16px.
           Lock to 16px on small viewports while still allowing tighter type
           on desktop. */
        .tommy-textarea {
          font-size: 16px;
          /* CSS-native textarea auto-grow. Compositor handles size on the
             GPU thread, so typing never blocks the main thread or
             re-rasterizes overlapping backdrop-blur surfaces. Browsers
             without support (very old iOS / Android WebView) fall back to
             the rows={1} sizing with internal scroll — degraded but
             functional, no crash, no lag. */
          field-sizing: content;
        }
        @media (min-width: 768px) {
          .tommy-textarea { font-size: 15px; }
        }
        .tommy-scroll::-webkit-scrollbar { width: 6px; }
        .tommy-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 6px; }
        .tommy-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.08) transparent; }
      `}</style>

      {/* Use a *fixed* viewport height (not min-h) so the inner message
          list is the real scroll surface — that's what makes the
          auto-scroll-to-bottom effect below actually work, and keeps
          the composer pinned to the bottom while the conversation
          scrolls inside it. `100dvh` adapts to mobile browser chrome
          and the iOS soft keyboard.

          Tailwind arbitrary values forbid raw spaces inside `[...]`,
          so we use `_` which Tailwind rewrites to a space at build
          time. Critically, CSS `calc()` requires whitespace around
          its `-` operator — `calc(100dvh-80px)` is silently invalid
          and gets dropped, falling back to `height: auto`. */}
      <div
        className="h-[calc(100dvh_-_80px)] bg-black text-white flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Ambient gradient backdrop */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-60">
          <div
            className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(15, 40, 138, 0.18) 0%, rgba(0,0,0,0) 60%)',
              filter: 'blur(60px)',
            }}
          />
        </div>

        {/* `min-h-0` here for the same reason the message list has it, and it
            has to be on BOTH. A flex child defaults to `min-height: auto`,
            which refuses to shrink below its content — so without it this
            column grew to fit the whole transcript (measured 3895px inside a
            1274px parent) and the height constraint never reached the scroll
            area below. The list then sized to its own content, making
            scrollHeight === clientHeight: nothing to scroll, and everything
            past the fold unreachable. */}
        <div className="relative z-10 flex-1 min-h-0 flex flex-col w-full max-w-2xl mx-auto px-3 sm:px-5">
          {/* Header */}
          <header className="pt-4 pb-3 border-b border-white/[0.06]">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative flex-shrink-0">
                  <TommyAvatar specialist={specialist} sizeClass="w-10 h-10" shadowClass="shadow-lg" ariaHidden />
                  <span
                    className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-black"
                    aria-label={`${specialist.name} is online`}
                  />
                </div>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <h1 className="text-[16px] sm:text-[17px] font-semibold text-white tracking-tight truncate">
                      {specialist.name}
                    </h1>
                    <span className="text-[11px] uppercase tracking-[0.14em] text-white/40 font-normal hidden sm:inline">
                      Concierge
                    </span>
                  </div>
                  <p className="text-[12px] text-white/45 truncate">
                    Replies instantly
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Admin-only: jump back to the QA inbox view. */}
                {isAdmin ? (
                  <button
                    type="button"
                    onClick={() => setAdminView('inbox')}
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium text-white/55 hover:text-white px-2.5 py-1.5 rounded-full hover:bg-white/[0.06] transition-colors"
                    aria-label="Back to concierge inbox"
                  >
                    <Inbox className="w-3.5 h-3.5" strokeWidth={1.75} />
                    <span className="hidden sm:inline">Inbox</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={messages.length === 0 || isStreaming}
                  className="inline-flex items-center gap-1.5 text-[12px] font-normal text-white/55 hover:text-white px-2.5 py-1.5 rounded-full hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Start new chat"
                >
                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} />
                  <span className="hidden sm:inline">New chat</span>
                </button>
              </div>
            </div>

            {/* Focused-trip pill. Shown only when a specific trip is being
                discussed — i.e. the customer arrived from that trip's page
                via `/support?bookingId=…`.

                The "Pull up my trip" button that used to sit here when no
                trip was focused is gone: the concierge preloads every trip
                on the account before its first reply, so the button asked
                the customer to hand over information we already had. What
                remains is status, not a chore — which trip is in focus, and
                a way out of it. */}
            {bookingContext?.summary ? (
              <div className="mt-3 flex items-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/[0.09] border border-emerald-400/20 text-emerald-200/95 text-[12px] font-medium">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-300/90" strokeWidth={2} />
                  <span className="truncate max-w-[60vw] sm:max-w-none">
                    Looking at trip{' '}
                    <span className="font-semibold text-white">
                      {bookingContext.summary.booking_reference || 'on file'}
                    </span>
                    <span className="hidden sm:inline text-emerald-200/65"> · {bookingContext.summary.status_label}</span>
                  </span>
                  <button
                    type="button"
                    onClick={clearBookingContext}
                    className="ml-1 -mr-1 p-0.5 rounded-full text-emerald-200/60 hover:text-white hover:bg-white/10 transition-colors"
                    aria-label="Stop discussing this trip"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ) : null}
          </header>

          {/* Message scroll area — `min-h-0` is required so this flex
              child can actually shrink and become the scroll surface
              (without it, default `min-height: auto` would let the
              list push the column past 100dvh and the *page* would
              scroll instead). */}
          <div
            ref={attachScrollRef}
            onScroll={onTranscriptScroll}
            className="flex-1 min-h-0 overflow-y-auto tommy-scroll overscroll-contain py-5 space-y-4"
          >
            {/* Conversation first, affordances second.

                Tommy's opening greeting is a real message in this list, so
                it has to render ABOVE the quick prompts — otherwise the
                page opens with a grid of buttons and *then* Tommy says
                hello underneath them, which is backwards: nobody walks up
                to a concierge and gets handed a menu before the hello.
                Greeting, then things to tap, then the composer. */}
            {hasEarlier && authUserId ? (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={loadEarlier}
                  disabled={loadingEarlier}
                  className="text-[12px] text-white/55 hover:text-white px-3 py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] disabled:opacity-60 transition-colors"
                >
                  {loadingEarlier ? 'Loading…' : 'Show earlier messages'}
                </button>
              </div>
            ) : null}

            {messages.map((msg, idx) => {
              const isLast = idx === messages.length - 1;
              if (msg.role === 'team') {
                return <TeamBubble key={`msg-${idx}`} message={msg} />;
              }
              if (msg.role === 'assistant') {
                return (
                  <TommyBubble
                    key={`msg-${idx}`}
                    message={msg}
                    specialist={specialist}
                    isStreaming={isStreaming && isLast}
                    onPickFlightOption={handlePickFlightOption}
                    flightPickDisabled={isStreaming}
                  />
                );
              }
              return <UserBubble key={`msg-${idx}`} message={msg} />;
            })}

            <AnimatePresence initial={false}>
              {showWelcome && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
                  className="flex flex-col items-center text-center pt-1 pb-2"
                >
                  {/* The big avatar + "Hi, I'm Tommy." headline used to
                      live here. Tommy now opens the conversation with a
                      real chat bubble (see the opener effect), so a hero
                      that says hello a second time — in a different voice,
                      in a different shape, directly above the bubble where
                      he actually says it — reads as a landing page bolted
                      onto a chat. The greeting belongs in the conversation;
                      what's left here is the part the bubble can't do:
                      things to tap. */}
                  {/* The "Pull up my trip" card used to lead here, asking the
                      customer to pick a trip before anything could be
                      answered. The concierge now loads every trip on the
                      account before it replies, so the card was asking them
                      to do work already done — and framing a self-service
                      chore as the first step of a support conversation. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-md">
                    {QUICK_PROMPTS.map(({ icon: Icon, label, prompt }) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => sendMessage(prompt)}
                        className="group text-left flex items-start gap-2.5 p-3.5 rounded-2xl bg-white/[0.035] hover:bg-white/[0.07] border border-white/[0.06] hover:border-white/[0.12] transition-all duration-200"
                      >
                        <Icon className="w-4 h-4 text-white/45 group-hover:text-white/80 mt-0.5 flex-shrink-0 transition-colors" strokeWidth={1.5} />
                        <span className="text-[13.5px] text-white/75 group-hover:text-white leading-snug transition-colors">
                          {label}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* The concierge number and support@ mailto used to sit
                      here, before the customer had said a single word.
                      They were the fastest path off this page and into a
                      human's phone — on a screen whose entire purpose is
                      that Tommy answers first.

                      Reaching a person is now Stephanie's call, not a
                      button the customer can take a shortcut to (see the
                      handoff comment at the top of this file). Tommy will
                      surface it himself the moment she says a situation is
                      urgent. What's left here is the Help Center, which
                      costs nobody anything. */}
                  <div className="mt-8 pt-6 border-t border-white/[0.05] w-full max-w-md flex items-center justify-center gap-5 text-[12px] text-white/40">
                    <Link to="/help" className="inline-flex items-center gap-1.5 hover:text-white/70 transition-colors">
                      <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Browse the Help Center
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mx-auto max-w-md rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-[13px] text-red-200/90"
              >
                {error}
              </motion.div>
            )}

            {/* In-thread hand-off CTA: appears only once a handoff is
                actually on the table — either the customer asked for a
                human or Tommy offered. See `messageOffersHandoff` for
                the trigger logic. Tapping produces an AI summary in
                the customer's voice and opens the native SMS composer
                pre-filled, so the human concierge sees the situation
                the second the text lands. */}
            {hasConversation && !isStreaming && showHandoffPill && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="flex justify-center pt-1"
              >
                <button
                  type="button"
                  onClick={handoffToHuman}
                  disabled={isPreparingHandoff}
                  className="group inline-flex items-center gap-2 text-[12.5px] font-medium text-white/55 hover:text-white px-3.5 py-2 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/[0.16] disabled:opacity-60 disabled:cursor-wait transition-all"
                  aria-label="Text a human concierge with the chat context"
                >
                  {isPreparingHandoff ? (
                    <>
                      <TypingDots />
                      <span>Preparing context…</span>
                    </>
                  ) : (
                    <>
                      <MessageSquareText className="w-3.5 h-3.5" strokeWidth={1.75} />
                      <span>Text a human</span>
                    </>
                  )}
                </button>
              </motion.div>
            )}
          </div>

          {/*
            Composer lives in its own React.memo'd subtree so typing
            inside the textarea NEVER re-renders the Support tree above.
            See ChatComposerInner for the perf rationale.
          */}
          <ChatComposer
            disabled={isStreaming}
            isPreparingHandoff={isPreparingHandoff}
            onSend={sendMessage}
            onHandoff={handoffToHuman}
            specialistName={specialist?.name || 'Tommy'}
            draft={composerDraft}
          />
        </div>
      </div>
    </>
  );
}
